/*
 * File: useLogin.ts
 * Author: Austin Robinson
 * Date Created: 9/7/2025
 * Copyright (c) DrivingDoc 2025
 */
import { createMutation } from 'react-query-kit';

import { ErrorResponse, publicApi } from 'api';

/**
 * Parameters required to perform a login request.
 */
export interface LoginParams {
  /**
   * The username used for authentication.
   */
  username: string;

  /**
   * The password used for authentication.
   */
  password: string;
}

/**
 * Result returned from a successful login request.
 */
export interface LoginResult {
  /**
   * The access token issued upon successful login.
   */
  accessToken: string;
}

/**
 * Performs a login request to the public authentication API.
 * @param params - The login parameters (username and password).
 * @returns A promise resolving to the login result containing the access token.
 * @throws {ErrorResponse} If the API request fails.
 */
async function login({ username, password }: LoginParams): Promise<LoginResult> {
  const result = await publicApi.post<LoginResult>(`/public/auth/login`, {
    username,
    password,
  });

  return result.data;
}

/**
 * Custom React Query mutation hook for performing a login request.
 * @returns A mutation hook for logging in a user.
 * @example
 * ```tsx
 * const { mutate, isLoading, error } = useLogin({
 *   onSuccess: (data) => {
 *     console.log('Access token:', data.accessToken);
 *   },
 *   onError: (error) => {
 *     console.error('Login failed:', error);
 *   },
 * });
 *
 * mutate({ username: 'john', password: 'secret' });
 * ```
 */
const useLogin = createMutation<LoginResult, LoginParams, Partial<ErrorResponse>>({
  mutationFn: login,
});

export { useLogin };
